import { useEffect, useMemo, useState } from "react";
import { UserPlus, Users, Copy, Trash2, Mail, KeyRound, Plus, Eye, Download, Heart, HelpCircle, Pencil, Send, X, Tag, Check, ChevronRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, mediaUrl } from "../../lib/api";
import { safeCopy } from "../../lib/clipboard";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { TagAutocomplete } from "../../components/TagAutocomplete";
import { useConfirm } from "../../components/ConfirmDialog";

const PINK = "#DF1283";

// Brand-color rotation for tag chips — deterministic per tag name so the same tag always
// gets the same color across the app. Skips UN-ONYX (reserved for active/selected states).
const TAG_PALETTE = [
  { bg: "#4A0A66", fg: "#FFFFFF" }, // UN-GRAPE
  { bg: "#DF1283", fg: "#FFFFFF" }, // UN-BERRY
  { bg: "#0FFFE2", fg: "#000000" }, // UN-WATER
  { bg: "#FC861E", fg: "#FFFFFF" }, // UN-PUMPKIN
  { bg: "#D0FF0D", fg: "#000000" }, // UN-LIME
];
const tagColor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
};

export default function AdminClients() {
  const confirm = useConfirm();
  const [data, setData] = useState({ clients: [], pending_invites: [] });
  const [codes, setCodes] = useState([]);
  const [showSingle, setShowSingle] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [single, setSingle] = useState({ email: "", first_name: "", last_name: "", title: "", tags: [], expires_at: "", expires_days: "" });
  const [bulkEntries, setBulkEntries] = useState([{ first_name: "", last_name: "", email: "" }]);
  const [bulkExpires, setBulkExpires] = useState({ expires_at: "", expires_days: "" });
  const [bulkTags, setBulkTags] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkResending, setBulkResending] = useState(false);
  const [bulkReminding, setBulkReminding] = useState(false);
  const [codeForm, setCodeForm] = useState({ label: "", max_uses: 25, expires_days: "", expires_at: "", tags: [] });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [showHowTo, setShowHowTo] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", new_password: "", access_code_id: "", expires_at: "", extend_days: "", tags: [] });
  const [emailingCode, setEmailingCode] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [editingCode, setEditingCode] = useState(null);
  const [editCodeForm, setEditCodeForm] = useState({ label: "", max_uses: 25, expires_at: "", tags: [] });
  const [tagFilter, setTagFilter] = useState(null); // active tag filter (null = show all)
  const [showTagManager, setShowTagManager] = useState(false);
  const [tagManagerSearch, setTagManagerSearch] = useState("");
  const [tagManagerSelected, setTagManagerSelected] = useState([]); // client ids
  const [tagManagerNewTag, setTagManagerNewTag] = useState("");
  const [tagManagerFocusTag, setTagManagerFocusTag] = useState(null); // filter clients to those with this tag
  const [tagLibrary, setTagLibrary] = useState([]); // [{name, client_count, invite_count, created_at}]
  const [libraryNewTag, setLibraryNewTag] = useState("");
  const [renamingTag, setRenamingTag] = useState(null);        // tag currently being renamed
  const [renameDraft, setRenameDraft] = useState("");
  const [membersOfCode, setMembersOfCode] = useState(null);    // {code, label, members[], ...}
  const [membersLoading, setMembersLoading] = useState(false);
  const [handoffCode, setHandoffCode] = useState(null);         // access-code obj we're building a PDF for
  const [handoffForm, setHandoffForm] = useState({ client_name: "", note: "" });
  const [handoffLoading, setHandoffLoading] = useState(false);

  const load = () => {
    api.get("/admin/clients").then((r) => setData(r.data));
    api.get("/admin/access-codes").then((r) => setCodes(r.data));
    api.get("/admin/tags").then((r) => setTagLibrary(r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  // Tag chips list — merge tag library (backend, includes standalone tags) with live counts from client data
  const tagCounts = useMemo(() => {
    const counts = new Map();
    (data?.clients || []).forEach((c) => (c.tags || []).forEach((t) => {
      counts.set(t, (counts.get(t) || 0) + 1);
    }));
    // Ensure standalone library tags (0 clients) still render
    (tagLibrary || []).forEach((t) => {
      if (!counts.has(t.name)) counts.set(t.name, 0);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [data?.clients, tagLibrary]);

  // Flat name list for TagAutocomplete suggestions
  const tagLibraryNames = useMemo(() => tagCounts.map(([n]) => n), [tagCounts]);

  const removeTagFromClient = async (client, tag) => {
    const next = (client.tags || []).filter((t) => t !== tag);
    try {
      await api.put(`/admin/clients/${client.id}`, { tags: next });
      // If we just removed the active filter tag from the last client that had it, clear the filter
      if (tagFilter === tag && !data.clients.some((c) => c.id !== client.id && (c.tags || []).includes(tag))) {
        setTagFilter(null);
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteTagGlobally = async (tag) => {
    const count = (data?.clients || []).filter((c) => (c.tags || []).includes(tag)).length;
    const confirmMsg = count > 0
      ? `Removes #${tag} from ${count} client${count === 1 ? "" : "s"} (and any pending invites). This cannot be undone.`
      : `Delete #${tag}? This cannot be undone.`;
    if (!(await confirm({ title: `Delete #${tag}?`, message: confirmMsg, tone: "danger", confirmText: "Delete tag" }))) return;
    try {
      await api.delete(`/admin/tags/${encodeURIComponent(tag)}`);
      // Optimistically strip from local state so the UI feels instant
      setData((prev) => ({
        ...prev,
        clients: (prev.clients || []).map((c) => ({ ...c, tags: (c.tags || []).filter((t) => t !== tag) })),
        pending_invites: (prev.pending_invites || []).map((p) => ({ ...p, tags: (p.tags || []).filter((t) => t !== tag) })),
      }));
      setTagLibrary((prev) => prev.filter((t) => t.name !== tag));
      if (tagFilter === tag) setTagFilter(null);
      if (tagManagerFocusTag === tag) setTagManagerFocusTag(null);
      toast.success(`Deleted #${tag} globally`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startRenameTag = (tag) => {
    setRenamingTag(tag);
    setRenameDraft(tag);
  };
  const cancelRenameTag = () => {
    setRenamingTag(null);
    setRenameDraft("");
  };
  const commitRenameTag = async () => {
    const from = renamingTag;
    const to = renameDraft.trim();
    if (!from || !to) return cancelRenameTag();
    if (to === from) return cancelRenameTag();
    if (to.length > 30) {
      toast.error("Tag must be 30 characters or fewer");
      return;
    }
    const mergesInto = tagCounts.some(([n]) => n !== from && n.toLowerCase() === to.toLowerCase());
    if (mergesInto) {
      const ok = await confirm({
        title: `Merge #${from} into #${to}?`,
        message: `#${to} already exists. Renaming will move every client tagged #${from} into #${to}.`,
        tone: "warn",
        confirmText: "Merge tags",
      });
      if (!ok) return;
    }
    try {
      const { data: res } = await api.put(`/admin/tags/${encodeURIComponent(from)}/rename`, { new_name: to });
      const bits = [];
      if (res.users_renamed || res.users_deduped) bits.push(`${(res.users_renamed || 0) + (res.users_deduped || 0)} client${((res.users_renamed || 0) + (res.users_deduped || 0)) === 1 ? "" : "s"}`);
      if (res.invites_renamed || res.invites_deduped) bits.push(`${(res.invites_renamed || 0) + (res.invites_deduped || 0)} pending invite${((res.invites_renamed || 0) + (res.invites_deduped || 0)) === 1 ? "" : "s"}`);
      if (res.codes_renamed || res.codes_deduped) bits.push(`${(res.codes_renamed || 0) + (res.codes_deduped || 0)} access code${((res.codes_renamed || 0) + (res.codes_deduped || 0)) === 1 ? "" : "s"}`);
      toast.success(res.merged
        ? `Merged #${from} into #${to}${bits.length ? " · " + bits.join(", ") : ""}`
        : `Renamed #${from} → #${to}${bits.length ? " · " + bits.join(", ") : ""}`);
      if (tagFilter === from) setTagFilter(to);
      if (tagManagerFocusTag === from) setTagManagerFocusTag(to);
      cancelRenameTag();
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createLibraryTag = async () => {
    const name = libraryNewTag.trim();
    if (!name) return;
    if (name.length > 30) {
      toast.error("Tag must be 30 characters or fewer");
      return;
    }
    try {
      const { data: res } = await api.post("/admin/tags", { name });
      if (res.already_exists) {
        toast.info(`#${res.name} already exists in your library`);
      } else {
        toast.success(`Added #${res.name} to your tag library`);
      }
      setLibraryNewTag("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyTagToSelected = async (tag) => {
    if (!tag || tagManagerSelected.length === 0) return;
    try {
      const { data: res } = await api.put("/admin/tags/apply", { tag, client_ids: tagManagerSelected });
      toast.success(`Applied #${tag} to ${res.updated} client${res.updated === 1 ? "" : "s"}`);
      setTagManagerSelected([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openMembersModal = async (c) => {
    setMembersOfCode({ ...c, members: [], _loading: true });
    setMembersLoading(true);
    try {
      const { data: res } = await api.get(`/admin/access-codes/${c.id}/members`);
      setMembersOfCode(res);
    } catch (e) {
      toast.error(apiError(e));
      setMembersOfCode(null);
    } finally {
      setMembersLoading(false);
    }
  };
  const refreshMembers = async () => {
    if (!membersOfCode) return;
    try {
      const { data: res } = await api.get(`/admin/access-codes/${membersOfCode.id}/members`);
      setMembersOfCode(res);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const remindOneMember = async (member) => {
    try {
      const { data: res } = await api.post("/admin/clients/signin-reminder", { client_ids: [member.id] });
      if (res.sent) toast.success(`Login help emailed to ${member.email}`);
      else if (res.skipped) toast.info(`Skipped ${member.email} · missing first name`);
      else toast.error(`Failed to email ${member.email}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const remindAllMembers = async () => {
    if (!membersOfCode || !membersOfCode.members?.length) return;
    const ids = membersOfCode.members.filter((m) => m.access_enabled !== false).map((m) => m.id);
    if (!ids.length) return toast.info("No active members to remind");
    if (!(await confirm({
      title: "Send login reminders?",
      message: `We'll email a sign-in reminder to all ${ids.length} active member${ids.length === 1 ? "" : "s"} on code ${membersOfCode.code}. It contains the login URL and a password-reset link — no password is ever leaked.`,
      tone: "send",
      confirmText: `Send ${ids.length} email${ids.length === 1 ? "" : "s"}`,
    }))) return;
    try {
      const { data: res } = await api.post("/admin/clients/signin-reminder", { client_ids: ids });
      toast.success(`Sent ${res.sent} · ${res.failed || 0} failed · ${res.skipped || 0} skipped`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const openMemberFromCode = (member) => {
    // Close the members modal, then open the client detail modal for this member
    setMembersOfCode(null);
    setTimeout(() => openClient(member), 100);
  };

  const openHandoff = (c) => {
    setHandoffCode(c);
    setHandoffForm({ client_name: "", note: "" });
  };
  const downloadHandoffPdf = async () => {
    if (!handoffCode) return;
    setHandoffLoading(true);
    try {
      const params = new URLSearchParams();
      if (handoffForm.client_name.trim()) params.set("client_name", handoffForm.client_name.trim());
      if (handoffForm.note.trim()) params.set("note", handoffForm.note.trim());
      const resp = await api.get(`/admin/access-codes/${handoffCode.id}/handoff.pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `UnCloned-Access-${handoffCode.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success("Handoff PDF downloaded — forward it to the client");
      setHandoffCode(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setHandoffLoading(false);
    }
  };

  const copyLink = (link) => safeCopy(link, "Invite link copied — send it to your client");

  const inviteSingle = async (e) => {
    e.preventDefault();
    const first_name = single.first_name.trim();
    const last_name = single.last_name.trim();
    if (!first_name || !last_name) {
      toast.error("First name and last name are required");
      return;
    }
    try {
      const tags = Array.isArray(single.tags) ? single.tags : [];
      const payload = { email: single.email, first_name, last_name, tags };
      if (single.title) payload.title = single.title;
      if (single.expires_at) payload.expires_at = single.expires_at;
      else if (single.expires_days) payload.expires_days = Number(single.expires_days);
      const { data: res } = await api.post("/admin/clients/invite", payload);
      setShowSingle(false);
      setSingle({ email: "", first_name: "", last_name: "", title: "", tags: [], expires_at: "", expires_days: "" });
      load();
      if (res.email_status?.ok) {
        toast.success(`Invite email sent to ${single.email}`);
      } else if (res.status === "created") {
        toast.success("Invite created — email did not send, link copied to clipboard");
        await copyLink(res.invite_link);
      } else {
        await copyLink(res.invite_link);
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    // Detect header row
    const first = lines[0].toLowerCase();
    const hasHeader = /first[_\s]?name/.test(first) && /email/.test(first);
    let start = 0;
    let idxFirst = 0, idxLast = 1, idxEmail = 2;
    if (hasHeader) {
      const hdr = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[_\s]+/g, ""));
      idxFirst = hdr.findIndex((h) => h === "firstname" || h === "first");
      idxLast = hdr.findIndex((h) => h === "lastname" || h === "last");
      idxEmail = hdr.findIndex((h) => h === "email" || h === "emailaddress");
      if (idxFirst < 0 || idxLast < 0 || idxEmail < 0) {
        toast.error("CSV must have columns: first_name, last_name, email");
        return [];
      }
      start = 1;
    }
    const rows = [];
    for (let i = start; i < lines.length; i++) {
      const parts = lines[i].split(",").map((p) => p.trim().replace(/^"|"$/g, ""));
      rows.push({
        first_name: (parts[idxFirst] || "").trim(),
        last_name: (parts[idxLast] || "").trim(),
        email: (parts[idxEmail] || "").trim(),
      });
    }
    return rows;
  };

  const onCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCsv(String(ev.target.result || ""));
      if (rows.length) {
        setBulkEntries(rows);
        toast.success(`Loaded ${rows.length} row${rows.length !== 1 ? "s" : ""} from CSV`);
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // allow re-uploading same file
  };

  const updateBulkRow = (idx, field, value) => {
    setBulkEntries((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addBulkRow = () => setBulkEntries((rows) => [...rows, { first_name: "", last_name: "", email: "" }]);
  const removeBulkRow = (idx) => setBulkEntries((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows));

  const inviteBulk = async (e) => {
    e.preventDefault();
    const cleaned = bulkEntries
      .map((r) => ({ first_name: r.first_name.trim(), last_name: r.last_name.trim(), email: r.email.trim().toLowerCase() }))
      .filter((r) => r.first_name || r.last_name || r.email);
    if (!cleaned.length) {
      toast.error("Add at least one recipient");
      return;
    }
    const missing = cleaned.filter((r) => !r.first_name || !r.last_name || !r.email);
    if (missing.length) {
      toast.error(`${missing.length} row${missing.length !== 1 ? "s are" : " is"} missing first name, last name, or email`);
      return;
    }
    try {
      const payload = { entries: cleaned };
      if (bulkExpires.expires_at) payload.expires_at = bulkExpires.expires_at;
      else if (bulkExpires.expires_days) payload.expires_days = Number(bulkExpires.expires_days);
      if (bulkTags.length) payload.tags = bulkTags;
      const { data: res } = await api.post("/admin/clients/invite-bulk", payload);
      setBulkResults(res.results);
      load();
      const created = res.results.filter((r) => r.status === "created").length;
      toast.success(`${created} invite email${created !== 1 ? "s" : ""} sent`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resendAllPending = async () => {
    if (!(await confirm({
      title: "Resend all pending invites?",
      message: `We'll fire a fresh activation email to all ${data.pending_invites.length} pending invite${data.pending_invites.length !== 1 ? "s" : ""}. Anyone who's already activated is skipped automatically.`,
      tone: "send",
      confirmText: `Resend ${data.pending_invites.length}`,
    }))) return;
    setBulkResending(true);
    try {
      const { data: res } = await api.post("/admin/invites/resend-all");
      toast.success(`Sent ${res.sent} of ${res.total} activation emails${res.failed ? ` — ${res.failed} failed` : ""}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBulkResending(false);
    }
  };

  const remindAllClients = async () => {
    const count = data.clients.filter((c) => c.access_enabled !== false).length;
    if (!count) return toast.info("No active clients to remind");
    if (!(await confirm({
      title: "Email everyone a sign-in reminder?",
      message: `We'll send a friendly login-help email to all ${count} active client${count !== 1 ? "s" : ""}. It contains the login URL + password-reset link — no passwords are ever leaked.`,
      tone: "send",
      confirmText: `Send to ${count}`,
    }))) return;
    setBulkReminding(true);
    try {
      const { data: res } = await api.post("/admin/clients/signin-reminder", {});
      const parts = [`Sent ${res.sent} of ${res.total}`];
      if (res.failed) parts.push(`${res.failed} failed`);
      if (res.skipped) parts.push(`${res.skipped} skipped (missing first name)`);
      toast.success(parts.join(" · "));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBulkReminding(false);
    }
  };

  const sendLoginHelp = async (client) => {
    if (client.access_enabled === false) {
      return toast.error(`${client.first_name || client.email} is deactivated — reactivate them first.`);
    }
    if (!client.first_name && !client.name) {
      return toast.error("Missing first name — edit the client to add one, then resend.");
    }
    try {
      const { data: res } = await api.post("/admin/clients/signin-reminder", { client_ids: [client.id] });
      if (res.sent) {
        toast.success(`Login help emailed to ${client.email}`);
      } else if (res.failed) {
        toast.error(`Email failed to send to ${client.email}`);
      } else {
        toast.info("Nothing sent — check the client's status.");
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const createCode = async (e) => {
    e.preventDefault();
    try {
      const payload = { label: codeForm.label, max_uses: Number(codeForm.max_uses) || 1 };
      if (codeForm.expires_at) {
        payload.expires_at = new Date(`${codeForm.expires_at}T23:59:59Z`).toISOString();
      } else if (codeForm.expires_days) {
        payload.expires_days = Number(codeForm.expires_days);
      }
      if (codeForm.tags && codeForm.tags.length) payload.tags = codeForm.tags;
      const { data: res } = await api.post("/admin/access-codes", payload);
      setShowCode(false);
      setCodeForm({ label: "", max_uses: 25, expires_days: "", expires_at: "", tags: [] });
      load();
      await safeCopy(res.code, `Code ${res.code} created & copied`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleCode = async (c) => {
    try {
      await api.put(`/admin/access-codes/${c.id}`, { active: !c.active });
      toast.success(!c.active ? "Code activated" : "Code deactivated");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeCode = async (c) => {
    if (!(await confirm({
      title: `Delete code ${c.code}?`,
      message: "Existing members keep their accounts — this just makes the code unusable for new signups.",
      tone: "danger",
      confirmText: "Delete code",
    }))) return;
    try {
      await api.delete(`/admin/access-codes/${c.id}`);
      toast.success("Code deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleAccess = async (c) => {
    try {
      const { data: res } = await api.post(`/admin/clients/${c.id}/toggle-access`);
      toast.success(res.access_enabled ? "Access enabled" : "Access disabled");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resend = async (inv) => {
    try {
      const { data: res } = await api.post(`/admin/invites/${inv.id}/resend`);
      load();
      if (res.email_status?.ok) {
        toast.success(`Fresh activation email sent to ${inv.email}`);
      } else if (res.email_configured === false) {
        toast.info("Link refreshed — email service not configured, link copied to clipboard");
        await copyLink(res.invite_link);
      } else {
        toast.warning(`Link refreshed but email did not send (${res.email_status?.error || "unknown reason"}) — link copied to clipboard`);
        await copyLink(res.invite_link);
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const revoke = async (inv) => {
    if (!(await confirm({
      title: "Revoke this invite?",
      message: `The activation link for ${inv.email} will stop working immediately. You can send a fresh invite later.`,
      tone: "danger",
      confirmText: "Revoke invite",
    }))) return;
    try {
      await api.delete(`/admin/invites/${inv.id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openClient = async (c) => {
    setSelectedClient(c);
    setClientDetail(null);
    try {
      const { data } = await api.get(`/admin/clients/${c.id}`);
      setClientDetail(data);
    } catch (err) {
      toast.error(apiError(err));
      setSelectedClient(null);
    }
  };

  const openEditClient = (c) => {
    setEditForm({
      first_name: c.first_name || (c.name ? c.name.split(" ")[0] : ""),
      last_name: c.last_name || (c.name ? c.name.split(" ").slice(1).join(" ") : ""),
      title: c.title || "",
      email: c.email,
      new_password: "",
      access_code_id: clientDetail?.user?.access_code?.id || "",
      expires_at: clientDetail?.user?.expires_at ? clientDetail.user.expires_at.slice(0, 10) : "",
      extend_days: "",
      tags: (c.tags || []).slice(),
    });
    setEditingClient(c);
  };

  const saveClient = async (e) => {
    e.preventDefault();
    const first_name = editForm.first_name.trim();
    const last_name = editForm.last_name.trim();
    if (!first_name || !last_name) {
      toast.error("First name and last name are required");
      return;
    }
    const payload = {};
    if (first_name !== (editingClient.first_name || "")) payload.first_name = first_name;
    if (last_name !== (editingClient.last_name || "")) payload.last_name = last_name;
    if ((editForm.title || "") !== (editingClient.title || "")) payload.title = editForm.title || "";
    if (editForm.email && editForm.email !== editingClient.email) payload.email = editForm.email;
    if (editForm.new_password) payload.new_password = editForm.new_password;
    const currentCodeId = clientDetail?.user?.access_code?.id || "";
    if (editForm.access_code_id !== currentCodeId) {
      if (editForm.access_code_id === "") payload.clear_access_code = true;
      else payload.access_code_id = editForm.access_code_id;
    }
    // Expiry logic: extend_days wins if provided; else absolute date set/change; else clear if user emptied it
    if (editForm.extend_days && Number(editForm.extend_days) !== 0) {
      payload.extend_days = Number(editForm.extend_days);
    } else {
      const currentUserExp = clientDetail?.user?.user_expires_at ? clientDetail.user.user_expires_at.slice(0, 10) : "";
      if (editForm.expires_at !== currentUserExp) {
        if (editForm.expires_at) {
          payload.expires_at = new Date(`${editForm.expires_at}T23:59:59Z`).toISOString();
        } else if (currentUserExp) {
          payload.clear_expiry = true;
        }
      }
    }
    // Tags — always send if changed (array from TagAutocomplete)
    const newTags = Array.isArray(editForm.tags) ? editForm.tags : [];
    const currentTags = (editingClient.tags || []).slice();
    if (JSON.stringify(newTags) !== JSON.stringify(currentTags)) {
      payload.tags = newTags;
    }
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }
    try {
      await api.put(`/admin/clients/${editingClient.id}`, payload);
      toast.success("Client updated");
      setEditingClient(null);
      load();
      if (selectedClient?.id === editingClient.id) openClient({ ...editingClient, ...payload });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteClient = async (c) => {
    if (!(await confirm({
      title: `Delete ${c.email}?`,
      message: "This removes their account, favorites, notes, and activity history. Cannot be undone.",
      tone: "danger",
      confirmText: "Delete client",
    }))) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client deleted");
      setEditingClient(null);
      setSelectedClient(null);
      setClientDetail(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const emailCode = async (e) => {
    e.preventDefault();
    const recipients = recipientsText.split(/\r?\n+/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) return toast.error("Add at least one recipient");
    try {
      const { data: res } = await api.post(`/admin/access-codes/${emailingCode.id}/email`, { recipients });
      const ok = res.results.filter((r) => r.ok).length;
      const failed = res.results.length - ok;
      if (ok) toast.success(`${ok} email${ok > 1 ? "s" : ""} sent`);
      if (failed) toast.error(`${failed} failed — check spam / verified domain`);
      setEmailingCode(null);
      setRecipientsText("");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEditCode = (c) => {
    setEditCodeForm({
      label: c.label || "",
      max_uses: c.max_uses,
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
      tags: (c.tags || []).slice(),
    });
    setEditingCode(c);
  };

  const saveCode = async (e) => {
    e.preventDefault();
    const payload = {};
    if (editCodeForm.label !== (editingCode.label || "")) payload.label = editCodeForm.label;
    if (Number(editCodeForm.max_uses) !== editingCode.max_uses) payload.max_uses = Number(editCodeForm.max_uses);
    const currentDate = editingCode.expires_at ? editingCode.expires_at.slice(0, 10) : "";
    if (editCodeForm.expires_at !== currentDate) {
      if (editCodeForm.expires_at) {
        payload.expires_at = new Date(`${editCodeForm.expires_at}T23:59:59Z`).toISOString();
      } else if (currentDate) {
        payload.clear_expiry = true;
      }
    }
    const currentCodeTags = (editingCode.tags || []).slice();
    const newCodeTags = Array.isArray(editCodeForm.tags) ? editCodeForm.tags : [];
    if (JSON.stringify(currentCodeTags) !== JSON.stringify(newCodeTags)) {
      payload.tags = newCodeTags;
    }
    if (Object.keys(payload).length === 0) return toast.info("No changes to save");
    try {
      await api.put(`/admin/access-codes/${editingCode.id}`, payload);
      toast.success("Access code updated");
      setEditingCode(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="fade-up max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#000000]">Clients & Access</h1>
          <p className="text-gray-500 mt-1 text-sm">Invite clients one-by-one, in bulk, or hand out group access codes. Invite emails send automatically — you can also copy links to send from your own inbox.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="add-client-btn" onClick={() => setShowSingle(true)}
            className="flex items-center gap-2 bg-[#000000] text-white rounded-full px-5 py-2.5 font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#4A0A66] transition-colors">
            <UserPlus size={15} /> Add Client
          </button>
          <button data-testid="bulk-invite-btn" onClick={() => { setBulkResults(null); setShowBulk(true); }}
            className="flex items-center gap-2 bg-[#D0FF0D] text-[#000000] rounded-full px-5 py-2.5 font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#0FFFE2] transition-colors">
            <Users size={15} /> Bulk Invite
          </button>
          <button data-testid="create-access-code-btn" onClick={() => setShowCode(true)}
            className="flex items-center gap-2 text-white rounded-full px-5 py-2.5 font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#000000] transition-colors" style={{ background: PINK }}>
            <KeyRound size={15} /> New Access Code
          </button>
          <button data-testid="manage-tags-btn" onClick={() => setShowTagManager(true)}
            className="flex items-center gap-2 bg-[#4A0A66] text-white rounded-full px-5 py-2.5 font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#000000] transition-colors">
            <Tag size={15} /> Manage Tags
          </button>
        </div>
      </div>

      <h2 className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400 mt-10 flex items-center gap-2">
        Access codes ({codes.length})
        <button data-testid="access-codes-help-btn" onClick={() => setShowHowTo(true)}
          className="text-gray-400 hover:text-[#DF1283] transition-colors" title="How access codes work">
          <HelpCircle size={13} />
        </button>
      </h2>
      <p className="text-xs text-gray-400 mt-1">Share one code with a whole group (e.g. a workshop). Each code is capped at a set number of accounts and can expire or be switched off anytime.</p>
      <div className="mt-3 space-y-2" data-testid="access-codes-list">
        {codes.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-4 bg-white border border-gray-200 rounded-xl p-4" data-testid={`access-code-row-${c.code}`}>
            <KeyRound size={16} className="shrink-0" style={{ color: PINK }} />
            <div className="flex-1 min-w-[160px]">
              <div className="font-mono font-bold text-sm text-[#000000] tracking-wider">{c.code}</div>
              <div className="text-xs text-gray-400">
                {c.label || "—"}{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
              </div>
            </div>
            <button
              data-testid={`code-usage-${c.code}`}
              onClick={() => openMembersModal(c)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${c.used_count >= c.max_uses ? "text-[#DF1283]" : "text-[#000000]"} bg-gray-100 hover:bg-[#0FFFE2] transition-colors flex items-center gap-1.5`}
              title={c.used_count > 0 ? `View the ${c.used_count} member${c.used_count === 1 ? "" : "s"} who signed up with this code` : "No members have used this code yet"}
            >
              {c.used_count} / {c.max_uses} used
              <ChevronRight size={11} strokeWidth={3} className="opacity-60" />
            </button>
            <button data-testid={`copy-code-btn-${c.code}`} onClick={() => safeCopy(c.code, `Code ${c.code} copied`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#000000] hover:bg-[#0FFFE2] transition-colors">
              <Copy size={12} /> Copy
            </button>
            <button data-testid={`email-code-btn-${c.code}`} onClick={() => { setEmailingCode(c); setRecipientsText(""); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#000000] hover:bg-[#D0FF0D] transition-colors" title="Email this code to people">
              <Send size={12} /> Email
            </button>
            <button data-testid={`edit-code-btn-${c.code}`} onClick={() => openEditCode(c)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#000000] hover:bg-[#FC861E] hover:text-white transition-colors" title="Edit code">
              <Pencil size={12} /> Edit
            </button>
            <button data-testid={`handoff-pdf-btn-${c.code}`} onClick={() => openHandoff(c)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#000000] hover:bg-[#4A0A66] hover:text-white transition-colors" title="Download a branded PDF handoff for the client">
              <FileDown size={12} /> PDF
            </button>
            <Switch data-testid={`code-active-switch-${c.code}`} checked={c.active} onCheckedChange={() => toggleCode(c)} />
            <button data-testid={`delete-code-btn-${c.code}`} onClick={() => removeCode(c)}
              className="p-2 rounded-full hover:bg-[#DF1283] text-gray-400 hover:text-white transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
        {codes.length === 0 && <div className="text-sm text-gray-400 py-4">No access codes yet — create one for your next group or workshop.</div>}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400">Pending invites ({data.pending_invites.length})</h2>
        {data.pending_invites.length > 0 && (
          <button
            data-testid="resend-all-invites-btn"
            onClick={resendAllPending}
            disabled={bulkResending}
            className="flex items-center gap-2 bg-white border border-gray-200 text-[#000000] rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-[#D0FF0D] hover:border-[#D0FF0D] transition-colors disabled:opacity-60"
          >
            <Send size={12} /> {bulkResending ? "Sending…" : `Resend all (${data.pending_invites.length})`}
          </button>
        )}
      </div>
      <div className="mt-3 space-y-2" data-testid="pending-invites-list">
        {data.pending_invites.map((inv) => (
          <div key={inv.id} className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4" data-testid={`invite-row-${inv.email}`}>
            <Mail size={16} className="text-[#FC861E] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#000000] truncate">
                {[inv.first_name, inv.last_name].filter(Boolean).join(" ") || inv.name || inv.email}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {inv.email} · Expires {new Date(inv.expires_at).toLocaleDateString()}
              </div>
            </div>
            <button data-testid={`resend-invite-btn-${inv.email}`} onClick={() => resend(inv)} title="Send a fresh activation email"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#000000] hover:bg-[#D0FF0D] transition-colors">
              <Send size={12} /> Resend email
            </button>
            <button data-testid={`copy-invite-btn-${inv.email}`} onClick={() => copyLink(inv.invite_link)} title="Copy the activation link to share manually"
              className="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors"><Copy size={14} /></button>
            <button data-testid={`revoke-invite-btn-${inv.email}`} onClick={() => revoke(inv)} title="Revoke this invite"
              className="p-2 rounded-full hover:bg-[#DF1283] text-gray-400 hover:text-white transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
        {data.pending_invites.length === 0 && <div className="text-sm text-gray-400 py-4">No pending invites.</div>}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400">
          Clients ({tagFilter ? `${data.clients.filter((c) => (c.tags || []).includes(tagFilter)).length} of ${data.clients.length}` : data.clients.length})
        </h2>
        {data.clients.length > 0 && (
          <button
            data-testid="signin-reminder-all-btn"
            onClick={remindAllClients}
            disabled={bulkReminding}
            className="flex items-center gap-2 bg-white border border-gray-200 text-[#000000] rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-[#0FFFE2] hover:border-[#0FFFE2] transition-colors disabled:opacity-60"
            title="Send a sign-in reminder to every active client (no password included, safe to send anytime)"
          >
            <Mail size={12} /> {bulkReminding ? "Sending…" : "Email sign-in reminder to all"}
          </button>
        )}
      </div>
      {tagCounts.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 items-center" data-testid="clients-tag-filter-bar">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 flex items-center gap-1"><Tag size={11} /> Filter by tag:</span>
          <button
            onClick={() => setTagFilter(null)}
            data-testid="tag-filter-all"
            className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${tagFilter === null ? "bg-[#000000] text-white" : "bg-white border border-[#0FFFE2] text-[#000000] hover:bg-[#0FFFE2]"}`}
          >
            All · {data.clients.length}
          </button>
          {tagCounts.map(([t, n]) => {
            const col = tagColor(t);
            const active = tagFilter === t;
            return (
              <button
                key={t}
                onClick={() => setTagFilter(active ? null : t)}
                data-testid={`tag-filter-${t}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold transition-transform hover:scale-105"
                style={active
                  ? { background: "#000000", color: "#FFFFFF", boxShadow: `0 0 0 2px ${col.bg}` }
                  : { background: col.bg, color: col.fg }}
              >
                #{t} · {n}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-3 space-y-2" data-testid="clients-list">
        {data.clients.filter((c) => !tagFilter || (c.tags || []).includes(tagFilter)).map((c) => (
          <div key={c.id} className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#0FFFE2] transition-colors cursor-pointer" data-testid={`client-row-${c.email}`} onClick={() => openClient(c)}>
            <div className="w-9 h-9 rounded-full bg-[#4A0A66] text-white flex items-center justify-center font-display font-black text-sm shrink-0" data-testid={`client-avatar-${c.email}`}>
              {((c.first_name || c.name || c.email)[0] || "?").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#000000] truncate">
                {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—"}
              </div>
              <div className="text-xs text-gray-400 truncate">{c.email}</div>
              {c.tags && c.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`client-tags-${c.email}`}>
                  {c.tags.map((t) => {
                    const col = tagColor(t);
                    return (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: col.bg, color: col.fg }}
                      >
                        #{t}
                        <button
                          type="button"
                          data-testid={`client-tag-remove-${c.email}-${t}`}
                          onClick={(e) => { e.stopPropagation(); removeTagFromClient(c, t); }}
                          className="hover:bg-black/20 rounded-full p-0.5 -mr-0.5 transition-colors"
                          title={`Remove #${t}`}
                          aria-label={`Remove tag ${t}`}
                        >
                          <X size={9} strokeWidth={3} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {c.expires_at && (
              <span
                className={`hidden md:inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] ${
                  c.expired
                    ? "bg-[#DF1283] text-white"
                    : "bg-[#FC861E] text-white"
                }`}
                title={`Access expires ${new Date(c.expires_at).toLocaleDateString()}`}
                data-testid={`client-days-remaining-${c.email}`}
              >
                {c.expired ? "Expired" : c.days_remaining === 0 ? "Expires today" : c.days_remaining === 1 ? "1 day left" : `${c.days_remaining} days left`}
              </span>
            )}
            <span
              className={`hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] ${
                c.signup_source === "code" ? "bg-[#4A0A66] text-white" : "bg-[#0FFFE2] text-[#000000]"
              }`}
              title={c.signup_source === "code" ? `Joined via access code ${c.access_code?.code || ""}${c.access_code?.label ? " · " + c.access_code.label : ""}` : "Manually added by admin — no access code, permanent access"}
              data-testid={`client-source-${c.email}`}
            >
              {c.signup_source === "code" ? (c.access_code?.code || "Code") : "Manual"}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${c.access_enabled ? "bg-[#D0FF0D] text-[#000000]" : "bg-[#DF1283] text-white"}`}>
              {c.access_enabled ? "Active" : "Disabled"}
            </span>
            <button
              data-testid={`send-login-help-${c.email}`}
              onClick={(e) => { e.stopPropagation(); sendLoginHelp(c); }}
              title="Email this client a sign-in reminder + password reset link"
              className="p-2 rounded-full hover:bg-[#0FFFE2] text-gray-400 hover:text-[#000000] transition-colors"
              aria-label={`Send login help to ${c.email}`}
            >
              <Mail size={14} />
            </button>
            <div onClick={(e) => e.stopPropagation()}>
              <Switch data-testid={`access-switch-${c.email}`} checked={c.access_enabled} onCheckedChange={() => toggleAccess(c)} />
            </div>
          </div>
        ))}
        {data.clients.length === 0 && <div className="text-sm text-gray-400 py-4">No clients yet — send your first invite or share an access code.</div>}
      </div>

      <Dialog open={showSingle} onOpenChange={setShowSingle}>
        <DialogContent data-testid="single-invite-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Add a client</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              We&rsquo;ll email them an activation link so they can set their own password and log in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={inviteSingle} className="space-y-4">
            <div className="grid grid-cols-[7rem_1fr_1fr] gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Title</label>
                <input data-testid="single-invite-title-input" value={single.title}
                  onChange={(e) => setSingle({ ...single, title: e.target.value })}
                  placeholder="Dr."
                  maxLength={12}
                  list="title-suggestions"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
                <datalist id="title-suggestions">
                  <option value="Dr." />
                  <option value="Mr." />
                  <option value="Mrs." />
                  <option value="Ms." />
                  <option value="Rev." />
                  <option value="Prof." />
                </datalist>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">First name <span className="text-[#DF1283]">*</span></label>
                <input data-testid="single-invite-first-name-input" required value={single.first_name}
                  onChange={(e) => setSingle({ ...single, first_name: e.target.value })}
                  placeholder="Sarah"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Last name <span className="text-[#DF1283]">*</span></label>
                <input data-testid="single-invite-last-name-input" required value={single.last_name}
                  onChange={(e) => setSingle({ ...single, last_name: e.target.value })}
                  placeholder="Johnson"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 -mt-2">Titles like <em>Dr.</em>, <em>Rev.</em>, <em>Prof.</em> go in the Title box — they&rsquo;ll appear in the greeting as <strong>Hi {single.title || "Dr."} {single.first_name || "Sarah"}</strong>,</p>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email <span className="text-[#DF1283]">*</span></label>
              <input data-testid="single-invite-email-input" type="email" required value={single.email}
                onChange={(e) => setSingle({ ...single, email: e.target.value })}
                placeholder="jane@brand.com"
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Tags <span className="font-normal normal-case text-gray-400 tracking-normal">· optional</span></label>
              <div className="mt-1.5">
                <TagAutocomplete
                  value={single.tags}
                  onChange={(tags) => setSingle({ ...single, tags })}
                  library={tagLibraryNames}
                  placeholder="Type to search or create a tag…"
                  testId="single-invite-tags"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Type to search your tag library. Unknown tags become new library entries automatically.</p>
            </div>

            {/* Expiration date */}
            <div className="rounded-lg border border-[#FC861E] bg-[#FC861E]/5 p-3">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Account expires <span className="font-normal normal-case text-gray-400 tracking-normal">· optional (leave blank for lifetime access)</span></label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1">Pick a date</div>
                  <input
                    data-testid="single-invite-expiry-date"
                    type="date"
                    value={single.expires_at}
                    min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                    onChange={(e) => setSingle({ ...single, expires_at: e.target.value, expires_days: "" })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1">Or days from today</div>
                  <input
                    data-testid="single-invite-expiry-days"
                    type="number"
                    min={1}
                    placeholder="e.g. 30"
                    value={single.expires_days}
                    onChange={(e) => setSingle({ ...single, expires_days: e.target.value, expires_at: "" })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[7, 30, 90, 180, 365].map((d) => (
                  <button
                    type="button"
                    key={d}
                    data-testid={`single-invite-expiry-quick-${d}`}
                    onClick={() => setSingle({ ...single, expires_days: String(d), expires_at: "" })}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] transition-colors ${String(single.expires_days) === String(d) ? "bg-[#FC861E] text-white" : "bg-white border border-[#FC861E] text-[#FC861E] hover:bg-[#FC861E] hover:text-white"}`}
                  >
                    {d} days
                  </button>
                ))}
                {(single.expires_at || single.expires_days) && (
                  <button
                    type="button"
                    onClick={() => setSingle({ ...single, expires_at: "", expires_days: "" })}
                    className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] bg-white border border-gray-300 text-gray-500 hover:bg-gray-100"
                  >
                    Clear · lifetime
                  </button>
                )}
              </div>
            </div>

            <button data-testid="single-invite-submit-btn" type="submit"
              className="w-full flex items-center justify-center gap-2 bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors">
              <Send size={14} /> Send activation email
            </button>
            <p className="text-[11px] text-gray-400 text-center">They&rsquo;ll receive a personalized email addressing them by first name with a link to activate their account and set a password.</p>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showBulk} onOpenChange={(o) => { setShowBulk(o); if (!o) { setBulkResults(null); setBulkExpires({ expires_at: "", expires_days: "" }); setBulkTags([]); } }}>
        <DialogContent className="max-w-2xl" data-testid="bulk-invite-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk invite clients</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Every client is greeted by first name in their activation email — first name, last name, and email are all required for every row. Upload a CSV or enter rows manually.
            </DialogDescription>
          </DialogHeader>
          {bulkResults ? (
            <div className="space-y-2 max-h-96 overflow-auto" data-testid="bulk-results-list">
              {bulkResults.map((r, i) => (
                <div key={i} className="flex items-center gap-3 text-sm border border-gray-200 rounded-lg p-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    r.status === "created" ? "bg-[#D0FF0D] text-black" : r.status === "invalid" ? "bg-[#DF1283] text-white" : "bg-gray-100 text-gray-500"}`}>
                    {r.status}
                  </span>
                  <span className="flex-1 truncate">{r.email}</span>
                  {r.reason && <span className="text-[11px] text-[#DF1283]">{r.reason}</span>}
                  {r.email_status?.ok && <span className="text-[11px] text-gray-500">email sent</span>}
                  {r.invite_link && (
                    <button onClick={() => copyLink(r.invite_link)} className="text-xs font-bold text-[#4A0A66] hover:underline">Copy link</button>
                  )}
                </div>
              ))}
              <button onClick={() => { setBulkResults(null); setBulkEntries([{ first_name: "", last_name: "", email: "" }]); }}
                className="w-full mt-2 bg-gray-100 text-[#000000] rounded-full px-6 py-2.5 font-bold text-sm hover:bg-gray-200 transition-colors">
                Invite more
              </button>
            </div>
          ) : (
            <form onSubmit={inviteBulk} className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center">
                <label className="inline-flex items-center gap-2 bg-[#0FFFE2] border border-[#0FFFE2] rounded-full px-4 py-2 text-xs font-bold text-[#000000] hover:bg-[#0FFFE2] cursor-pointer transition-colors" data-testid="bulk-csv-upload-label">
                  <input type="file" accept=".csv,text/csv" onChange={onCsvUpload} className="hidden" data-testid="bulk-csv-upload-input" />
                  Upload CSV
                </label>
                <span className="text-[11px] text-gray-500">Columns: <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">first_name, last_name, email</code></span>
              </div>

              <div className="space-y-2 max-h-80 overflow-auto pr-1" data-testid="bulk-entries-list">
                {bulkEntries.map((row, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-center" data-testid={`bulk-entry-row-${i}`}>
                    <input
                      value={row.first_name}
                      onChange={(e) => updateBulkRow(i, "first_name", e.target.value)}
                      placeholder="First name"
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                      data-testid={`bulk-first-name-${i}`}
                    />
                    <input
                      value={row.last_name}
                      onChange={(e) => updateBulkRow(i, "last_name", e.target.value)}
                      placeholder="Last name"
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                      data-testid={`bulk-last-name-${i}`}
                    />
                    <input
                      type="email"
                      value={row.email}
                      onChange={(e) => updateBulkRow(i, "email", e.target.value)}
                      placeholder="email@brand.com"
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                      data-testid={`bulk-email-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeBulkRow(i)}
                      disabled={bulkEntries.length === 1}
                      className="p-2 rounded-full text-gray-400 hover:bg-[#DF1283] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Remove row"
                      data-testid={`bulk-remove-row-${i}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <button type="button" onClick={addBulkRow} className="text-xs font-bold text-[#4A0A66] hover:text-[#000000] transition-colors" data-testid="bulk-add-row-btn">
                + Add another row
              </button>

              {/* Tags for every row in this batch */}
              <div className="rounded-lg border border-[#4A0A66] bg-[#4A0A66]/5 p-3">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Tags <span className="font-normal normal-case text-gray-400 tracking-normal">· applied to every client in this batch · optional</span></label>
                <div className="mt-2">
                  <TagAutocomplete
                    value={bulkTags}
                    onChange={setBulkTags}
                    library={tagLibraryNames}
                    placeholder="Type to search or create a tag…"
                    testId="bulk-invite-tags"
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Unknown tags become new library entries automatically.</p>
              </div>

              {/* Expiration for every row in this batch */}
              <div className="rounded-lg border border-[#FC861E] bg-[#FC861E]/5 p-3">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Account expires <span className="font-normal normal-case text-gray-400 tracking-normal">· applies to every client in this batch · optional (leave blank for lifetime)</span></label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input
                    data-testid="bulk-invite-expiry-date"
                    type="date"
                    value={bulkExpires.expires_at}
                    min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                    onChange={(e) => setBulkExpires({ expires_at: e.target.value, expires_days: "" })}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                  />
                  <input
                    data-testid="bulk-invite-expiry-days"
                    type="number"
                    min={1}
                    placeholder="Or days from today"
                    value={bulkExpires.expires_days}
                    onChange={(e) => setBulkExpires({ expires_days: e.target.value, expires_at: "" })}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[7, 30, 90, 180, 365].map((d) => (
                    <button
                      type="button"
                      key={d}
                      data-testid={`bulk-invite-expiry-quick-${d}`}
                      onClick={() => setBulkExpires({ expires_days: String(d), expires_at: "" })}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] transition-colors ${String(bulkExpires.expires_days) === String(d) ? "bg-[#FC861E] text-white" : "bg-white border border-[#FC861E] text-[#FC861E] hover:bg-[#FC861E] hover:text-white"}`}
                    >
                      {d} days
                    </button>
                  ))}
                  {(bulkExpires.expires_at || bulkExpires.expires_days) && (
                    <button
                      type="button"
                      onClick={() => setBulkExpires({ expires_at: "", expires_days: "" })}
                      className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] bg-white border border-gray-300 text-gray-500 hover:bg-gray-100"
                    >
                      Clear · lifetime
                    </button>
                  )}
                </div>
              </div>

              <button data-testid="bulk-invite-submit-btn" type="submit"
                className="w-full bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors">
                Send {bulkEntries.filter((r) => r.email).length || ""} activation email{bulkEntries.filter((r) => r.email).length === 1 ? "" : "s"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCode} onOpenChange={setShowCode}>
        <DialogContent data-testid="access-code-modal">
          <DialogHeader>
            <DialogTitle className="font-display">New Access Code</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Create a group code with a max-account cap and expiration. Share it — anyone with the code can register up to the cap.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createCode} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Label / group name</label>
              <input data-testid="access-code-label-input" required value={codeForm.label}
                onChange={(e) => setCodeForm({ ...codeForm, label: e.target.value })}
                placeholder="e.g. June Workshop Attendees"
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#DF1283]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Max accounts</label>
                <input data-testid="access-code-maxuses-input" type="number" min={1} required value={codeForm.max_uses}
                  onChange={(e) => setCodeForm({ ...codeForm, max_uses: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#DF1283]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Expires in (days)</label>
                <input data-testid="access-code-expiry-input" type="number" min={1} value={codeForm.expires_days}
                  onChange={(e) => setCodeForm({ ...codeForm, expires_days: e.target.value, expires_at: "" })}
                  placeholder="Never"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#DF1283]" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Or pick an exact expiration date</label>
              <input data-testid="access-code-expiry-date" type="date" value={codeForm.expires_at}
                onChange={(e) => setCodeForm({ ...codeForm, expires_at: e.target.value, expires_days: "" })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#DF1283]" />
              <p className="text-[11px] text-gray-400 mt-1">Use either field — an exact date overrides the day count.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Tags <span className="font-normal normal-case text-gray-400 tracking-normal">· optional · applied to every client who registers with this code</span></label>
              <div className="mt-1.5">
                <TagAutocomplete
                  value={codeForm.tags}
                  onChange={(tags) => setCodeForm({ ...codeForm, tags })}
                  library={tagLibraryNames}
                  placeholder="Type to search or create a tag…"
                  testId="access-code-tags"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Anyone who signs up with this code will inherit these tags automatically.</p>
            </div>
            <button data-testid="access-code-submit-btn" type="submit"
              className="w-full text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#000000] transition-colors flex items-center justify-center gap-2" style={{ background: PINK }}>
              <Plus size={15} /> Generate code
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!selectedClient} onOpenChange={(o) => { if (!o) { setSelectedClient(null); setClientDetail(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="client-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{[selectedClient?.first_name, selectedClient?.last_name].filter(Boolean).join(" ") || selectedClient?.name || selectedClient?.email || "Client"}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">{selectedClient?.email}</DialogDescription>
          </DialogHeader>
          {!clientDetail ? (
            <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: "views", label: "Views", icon: Eye, color: "#0FFFE2" },
                  { key: "downloads", label: "Downloads", icon: Download, color: "#D0FF0D" },
                  { key: "favorites", label: "Favorites", icon: Heart, color: "#DF1283" },
                  { key: "current_favorites", label: "Saved now", icon: Heart, color: "#4A0A66" },
                ].map(({ key, label, icon: Icon, color }) => (
                  <div key={key} className="bg-white border border-gray-200 rounded-xl p-3" data-testid={`client-stat-${key}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
                      <Icon size={13} style={{ color: color === "#D0FF0D" ? "#000000" : color }} />
                    </div>
                    <div className="font-display font-black text-2xl text-[#000000] mt-2 tabular-nums">{clientDetail.totals[key] ?? 0}</div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                <span className={`px-2 py-1 rounded-full font-bold ${clientDetail.user.access_enabled ? "bg-[#D0FF0D] text-black" : "bg-[#DF1283] text-white"}`}>
                  {clientDetail.user.access_enabled ? "Active" : "Disabled"}
                </span>
                {clientDetail.user.created_at && (
                  <span className="px-2 py-1 rounded-full bg-gray-100">Joined {new Date(clientDetail.user.created_at).toLocaleDateString()}</span>
                )}
                {clientDetail.user.access_code && (
                  <span className="px-2 py-1 rounded-full bg-gray-100">Code: {clientDetail.user.access_code.code}{clientDetail.user.access_code.label ? ` · ${clientDetail.user.access_code.label}` : ""}</span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                <button data-testid="edit-client-btn" onClick={() => openEditClient(clientDetail.user)}
                  className="flex items-center gap-2 bg-[#000000] text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-[#4A0A66] transition-colors">
                  <Pencil size={13} /> Edit client
                </button>
                <button data-testid="detail-send-login-help-btn" onClick={() => sendLoginHelp(clientDetail.user)}
                  className="flex items-center gap-2 bg-[#0FFFE2] text-[#000000] rounded-full px-4 py-2 text-xs font-bold hover:bg-[#4A0A66] hover:text-white transition-colors"
                  title="Email this client a sign-in reminder with the login URL + password reset link">
                  <Mail size={13} /> Send login help
                </button>
                <button data-testid="delete-client-btn" onClick={() => deleteClient(clientDetail.user)}
                  className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 rounded-full px-4 py-2 text-xs font-bold hover:bg-[#DF1283] hover:text-white hover:border-[#DF1283] transition-colors">
                  <Trash2 size={13} /> Delete
                </button>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400 mb-2">Recent activity</div>
                <div className="border border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto" data-testid="client-recent-activity">
                  {clientDetail.recent_activity.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-400" data-testid="client-recent-activity-empty">No activity yet.</div>
                  ) : (
                    clientDetail.recent_activity.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 px-4 py-2 border-b border-gray-50 last:border-0">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                          {r.ad_has_thumbnail && !r.ad_deleted ? (
                            <img src={mediaUrl(r.ad_id, "thumb")} alt="" className="w-full h-full object-cover" />
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#000000] truncate">
                            {r.ad_title}
                            {r.ad_deleted && <span className="ml-2 text-xs text-gray-400">(deleted)</span>}
                          </div>
                          <div className="text-[11px] text-gray-400">{new Date(r.created_at).toLocaleString()}</div>
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-1 rounded-full ${
                          r.type === "download" ? "bg-[#D0FF0D] text-black" :
                          r.type === "favorite" ? "bg-[#DF1283] text-white" :
                          "bg-[#0FFFE2] text-black"
                        }`}>
                          {r.type}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingClient} onOpenChange={(o) => !o && setEditingClient(null)}>
        <DialogContent data-testid="edit-client-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Edit client</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Update name, email, reset password, or reassign an access code.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveClient} className="space-y-4">
            <div className="grid grid-cols-[6rem_1fr_1fr] gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Title</label>
                <input data-testid="edit-client-title-input" value={editForm.title || ""}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Dr."
                  maxLength={12}
                  list="title-suggestions"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">First name <span className="text-[#DF1283]">*</span></label>
                <input data-testid="edit-client-first-name-input" required value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Last name <span className="text-[#DF1283]">*</span></label>
                <input data-testid="edit-client-last-name-input" required value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email</label>
              <input data-testid="edit-client-email-input" type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Reset password</label>
              <input data-testid="edit-client-password-input" type="text" placeholder="Leave blank to keep current" value={editForm.new_password}
                onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
              <p className="text-[11px] text-gray-400 mt-1">Min 8 characters. Share the new password with your client — they can log in immediately.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Access code</label>
              <select data-testid="edit-client-code-select" value={editForm.access_code_id}
                onChange={(e) => setEditForm({ ...editForm, access_code_id: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]">
                <option value="">— None —</option>
                {codes.map((c) => (
                  <option key={c.id} value={c.id}>{c.code}{c.label ? ` · ${c.label}` : ""}</option>
                ))}
              </select>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Access expiration</label>
              <div className="mt-2 text-[11px] text-gray-500">
                {clientDetail?.user?.expires_at ? (
                  <>Currently expires <strong className="text-[#000000]">{new Date(clientDetail.user.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</strong>
                  {clientDetail.user.expires_source === "code" && <span className="ml-1 text-gray-400">(from access code)</span>}
                  {clientDetail.user.expires_source === "user" && <span className="ml-1 text-[#DF1283]">(custom for this user)</span>}
                  </>
                ) : "No expiration set."}
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1.5">Set exact date</div>
                <input
                  type="date"
                  data-testid="edit-client-expires-date"
                  value={editForm.expires_at}
                  onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value, extend_days: "" })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1.5">Or add / remove days</div>
                <div className="flex flex-wrap gap-2">
                  {[-7, +7, +14, +30, +90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      data-testid={`edit-client-extend-${d}`}
                      onClick={() => setEditForm({ ...editForm, extend_days: String(d), expires_at: "" })}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                        editForm.extend_days === String(d) ? "bg-[#FC861E] text-white" : "bg-gray-100 text-[#000000] hover:bg-[#FC861E]"
                      }`}
                    >
                      {d > 0 ? `+${d}` : d} days
                    </button>
                  ))}
                  <input
                    type="number"
                    data-testid="edit-client-extend-custom"
                    placeholder="Custom"
                    value={editForm.extend_days}
                    onChange={(e) => setEditForm({ ...editForm, extend_days: e.target.value, expires_at: "" })}
                    className="w-24 rounded-full border border-gray-300 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Positive extends, negative shortens. Adds to the current expiry (or today if already expired). Overrides the access-code expiration for this user only.</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Tags <span className="font-normal normal-case text-gray-400 tracking-normal">· optional</span></label>
              <div className="mt-1.5">
                <TagAutocomplete
                  value={editForm.tags}
                  onChange={(tags) => setEditForm({ ...editForm, tags })}
                  library={tagLibraryNames}
                  placeholder="Type to search or create a tag…"
                  testId="edit-client-tags"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Type to search your tag library. Unknown tags become new library entries automatically.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button data-testid="edit-client-save-btn" type="submit"
                className="flex-1 bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors">
                Save changes
              </button>
              <button type="button" onClick={() => deleteClient(editingClient)}
                data-testid="edit-client-delete-btn"
                className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 rounded-full px-4 py-2.5 text-sm font-bold hover:bg-[#DF1283] hover:text-white hover:border-[#DF1283] transition-colors">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailingCode} onOpenChange={(o) => !o && setEmailingCode(null)}>
        <DialogContent data-testid="email-code-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Email access code</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Send <span className="font-mono font-bold text-[#000000]">{emailingCode?.code}</span> to one or more people. Enter one recipient per line — either <span className="font-mono">email@x.com</span> or <span className="font-mono">First Last &lt;email@x.com&gt;</span> to personalize the greeting.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={emailCode} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Recipients</label>
              <textarea data-testid="email-code-recipients-input" rows={6} required value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                placeholder={"Jane Doe <jane@brand.com>\nMike Ross <mike@brand.com>\nplain@brand.com"}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#D0FF0D]" />
              <p className="text-[11px] text-gray-400 mt-1">Replies go to <strong>info@unclonedmarketing.com</strong>.</p>
            </div>
            <button data-testid="email-code-submit-btn" type="submit"
              className="w-full flex items-center justify-center gap-2 bg-[#D0FF0D] text-[#000000] rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#0FFFE2] transition-colors">
              <Send size={14} /> Send emails
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingCode} onOpenChange={(o) => !o && setEditingCode(null)}>
        <DialogContent data-testid="edit-code-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Access Code</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Code <span className="font-mono font-bold text-[#000000]">{editingCode?.code}</span> — {editingCode?.used_count}/{editingCode?.max_uses} used.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveCode} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Group label</label>
              <input data-testid="edit-code-label-input" value={editCodeForm.label}
                onChange={(e) => setEditCodeForm({ ...editCodeForm, label: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Max accounts (add or remove licenses)</label>
              <input data-testid="edit-code-maxuses-input" type="number" min={editingCode?.used_count || 1} value={editCodeForm.max_uses}
                onChange={(e) => setEditCodeForm({ ...editCodeForm, max_uses: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]" />
              <p className="text-[11px] text-gray-400 mt-1">Cannot go lower than the {editingCode?.used_count || 0} already claimed.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Expiration date</label>
              <input data-testid="edit-code-expiry-date" type="date" value={editCodeForm.expires_at}
                onChange={(e) => setEditCodeForm({ ...editCodeForm, expires_at: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]" />
              <p className="text-[11px] text-gray-400 mt-1">Leave blank for no expiration.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Tags <span className="font-normal normal-case text-gray-400 tracking-normal">· applied to every future signup with this code</span></label>
              <div className="mt-1.5">
                <TagAutocomplete
                  value={editCodeForm.tags}
                  onChange={(tags) => setEditCodeForm({ ...editCodeForm, tags })}
                  library={tagLibraryNames}
                  placeholder="Type to search or create a tag…"
                  testId="edit-code-tags"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Changes apply to <strong>new</strong> signups only — existing members keep the tags they were originally given.</p>
            </div>
            <button data-testid="edit-code-save-btn" type="submit"
              className="w-full text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#000000] transition-colors flex items-center justify-center gap-2 bg-[#FC861E]">
              Save Changes
            </button>
            <p className="text-[11px] text-gray-500 text-center">Need to add more people? Just email the code to them — anyone with this code can register up to the max-account cap.</p>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showHowTo} onOpenChange={setShowHowTo}>
        <DialogContent className="max-w-lg" data-testid="access-codes-howto-modal">
          <DialogHeader>
            <DialogTitle className="font-display">How access codes work</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Sell workshop tickets or bulk-onboard clients without emailing invites one by one.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-[#000000] mt-2 list-decimal pl-5">
            <li><span className="font-semibold">Create a code.</span> Give it a label (e.g. &ldquo;June Workshop&rdquo;), pick a max number of accounts, and an optional expiry date.</li>
            <li><span className="font-semibold">Share the code.</span> Send the code (e.g. <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">UNC-DEMO25</span>) along with your registration link: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs break-all">{window.location.origin}/register</span></li>
            <li><span className="font-semibold">Members self-register.</span> Each person visits the link, enters the code, their name, email, and password. They&rsquo;re instantly logged in — no invite email needed.</li>
            <li><span className="font-semibold">Cap enforced automatically.</span> Once the code hits its max uses it stops accepting new signups. The counter is visible in the code row.</li>
            <li><span className="font-semibold">Kill switch.</span> Toggle a code off anytime to freeze new signups. Existing members keep their accounts and access. Toggle the client &ldquo;Active&rdquo; switch to revoke a single member.</li>
          </ol>
          <div className="text-xs text-gray-500 border-t border-gray-100 pt-3 mt-2">
            Tip: expired or deactivated codes reject registrations with a clear error, so it&rsquo;s safe to hand out codes on flyers, DMs, or landing pages.
          </div>
        </DialogContent>
      </Dialog>

      {/* Tag Manager — add / delete / apply tags in one place */}
      <Dialog open={showTagManager} onOpenChange={(o) => { setShowTagManager(o); if (!o) { setTagManagerSelected([]); setTagManagerSearch(""); setTagManagerNewTag(""); setTagManagerFocusTag(null); setLibraryNewTag(""); setRenamingTag(null); setRenameDraft(""); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tag-manager-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display font-black">Manage Tags</DialogTitle>
            <DialogDescription>Add a new tag, delete a tag, or apply a tag to your clients — all in one place.</DialogDescription>
          </DialogHeader>

          {/* ── Section 1 · Add a new tag ────────────────────────────── */}
          <section className="rounded-xl border-2 border-[#0FFFE2] bg-[#0FFFE2]/5 p-4" data-testid="tag-section-add">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0FFFE2] text-[#000000] text-xs font-black">1</span>
              <h3 className="text-sm font-black uppercase tracking-[0.1em] text-[#000000]">Add a new tag</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-3 ml-8">Save a tag to your library — you can apply it to clients later.</p>
            <div className="flex gap-2 ml-8">
              <input
                data-testid="tag-library-new-input"
                value={libraryNewTag}
                onChange={(e) => setLibraryNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createLibraryTag(); } }}
                placeholder="e.g. Kartra, Instagram, VIP"
                maxLength={30}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
              />
              <button
                data-testid="tag-library-create-btn"
                disabled={!libraryNewTag.trim()}
                onClick={createLibraryTag}
                className="bg-[#000000] text-white rounded-full px-5 py-2.5 font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#4A0A66] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Add Tag
              </button>
            </div>
          </section>

          {/* ── Section 2 · Tag Library (delete) ─────────────────────── */}
          <section className="rounded-xl border-2 border-[#4A0A66] bg-[#4A0A66]/5 p-4" data-testid="tag-section-library">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#4A0A66] text-white text-xs font-black">2</span>
              <h3 className="text-sm font-black uppercase tracking-[0.1em] text-[#000000]">Tag Library <span className="text-gray-500 font-bold normal-case tracking-normal">· {tagCounts.length}</span></h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-3 ml-8">Click a chip to filter clients below. Click ✎ to rename, or × to delete that tag everywhere.</p>
            <div className="flex flex-wrap gap-2 ml-8 min-h-[32px]">
              {tagCounts.length === 0 && <span className="text-xs text-gray-400 italic">No tags yet — add one above.</span>}
              {tagCounts.map(([t, n]) => {
                const isFocused = tagManagerFocusTag === t;
                const isRenaming = renamingTag === t;
                const col = tagColor(t);
                if (isRenaming) {
                  return (
                    <span
                      key={t}
                      data-testid={`tag-manager-chip-${t}`}
                      className="inline-flex items-center gap-1 rounded-full text-[11px] font-bold overflow-hidden px-2 py-0.5 border-2"
                      style={{ borderColor: col.bg, background: "#FFFFFF", color: col.bg }}
                    >
                      <span style={{ color: col.bg }}>#</span>
                      <input
                        autoFocus
                        data-testid={`tag-manager-rename-input-${t}`}
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); commitRenameTag(); }
                          if (e.key === "Escape") { e.preventDefault(); cancelRenameTag(); }
                        }}
                        maxLength={30}
                        className="bg-transparent outline-none w-24 text-[11px] font-bold"
                        style={{ color: col.bg }}
                      />
                      <button
                        type="button"
                        data-testid={`tag-manager-rename-save-${t}`}
                        onClick={commitRenameTag}
                        title="Save rename"
                        className="p-0.5 rounded-full hover:bg-black/10"
                        style={{ color: col.bg }}
                      >
                        <Check size={12} strokeWidth={3} />
                      </button>
                      <button
                        type="button"
                        data-testid={`tag-manager-rename-cancel-${t}`}
                        onClick={cancelRenameTag}
                        title="Cancel"
                        className="p-0.5 rounded-full hover:bg-black/10"
                        style={{ color: "#DF1283" }}
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </span>
                  );
                }
                return (
                  <span
                    key={t}
                    data-testid={`tag-manager-chip-${t}`}
                    className="inline-flex items-center rounded-full text-[11px] font-bold overflow-hidden"
                    style={isFocused
                      ? { background: "#000000", color: "#FFFFFF", boxShadow: `0 0 0 2px ${col.bg}` }
                      : { background: col.bg, color: col.fg }}
                  >
                    <button
                      type="button"
                      data-testid={`tag-manager-tag-${t}`}
                      onClick={() => setTagManagerFocusTag(isFocused ? null : t)}
                      className="pl-3 pr-2 py-1 hover:bg-black/15 transition-colors"
                      title={`${n} client${n === 1 ? "" : "s"} tagged · click to filter`}
                    >
                      #{t} · {n}
                    </button>
                    <button
                      type="button"
                      data-testid={`tag-manager-rename-${t}`}
                      onClick={(e) => { e.stopPropagation(); startRenameTag(t); }}
                      className="px-1 py-1 hover:bg-black/25 transition-colors"
                      title={`Rename #${t}`}
                      aria-label={`Rename tag ${t}`}
                    >
                      <Pencil size={10} strokeWidth={3} />
                    </button>
                    <button
                      type="button"
                      data-testid={`tag-manager-delete-${t}`}
                      onClick={(e) => { e.stopPropagation(); deleteTagGlobally(t); }}
                      className="pl-1 pr-2 py-1 hover:bg-black/25 transition-colors"
                      title={`Delete #${t} everywhere`}
                      aria-label={`Delete tag ${t} globally`}
                    >
                      <X size={11} strokeWidth={3} />
                    </button>
                  </span>
                );
              })}
            </div>
            {tagManagerFocusTag && (
              <button
                onClick={() => setTagManagerFocusTag(null)}
                className="mt-2 ml-8 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 hover:text-[#000000]"
              >
                × Clear filter · showing clients tagged &ldquo;#{tagManagerFocusTag}&rdquo;
              </button>
            )}
          </section>

          {/* ── Section 3 · Apply a tag to clients ──────────────────── */}
          <section className="rounded-xl border-2 border-[#FC861E] bg-[#FC861E]/5 p-4" data-testid="tag-section-apply">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#FC861E] text-[#000000] text-xs font-black">3</span>
              <h3 className="text-sm font-black uppercase tracking-[0.1em] text-[#000000]">Apply a tag to clients</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-3 ml-8">Pick clients, then click a library tag above OR type a fresh tag below.</p>

            <div className="ml-8">
              <input
                data-testid="tag-manager-search"
                value={tagManagerSearch}
                onChange={(e) => setTagManagerSearch(e.target.value)}
                placeholder="Search clients by name or email…"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
              />
              <div className="mt-2 border border-gray-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-gray-100 bg-white" data-testid="tag-manager-client-list">
                {data.clients
                  .filter((c) => {
                    if (tagManagerFocusTag && !(c.tags || []).includes(tagManagerFocusTag)) return false;
                    if (!tagManagerSearch) return true;
                    const q = tagManagerSearch.toLowerCase();
                    return c.email.toLowerCase().includes(q) ||
                           (c.first_name || "").toLowerCase().includes(q) ||
                           (c.last_name || "").toLowerCase().includes(q);
                  })
                  .slice(0, 200)
                  .map((c) => {
                    const checked = tagManagerSelected.includes(c.id);
                    return (
                      <label key={c.id} data-testid={`tag-manager-row-${c.email}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setTagManagerSelected((prev) => e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id));
                          }}
                          className="w-4 h-4 accent-[#4A0A66]"
                        />
                        <span className="flex-1 truncate">
                          <span className="font-semibold">{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || c.email}</span>
                          <span className="text-gray-400 ml-2 text-xs">{c.email}</span>
                        </span>
                        {c.tags && c.tags.length > 0 && (
                          <span className="text-[10px] text-[#4A0A66] font-bold shrink-0">{c.tags.length} tag{c.tags.length === 1 ? "" : "s"}</span>
                        )}
                      </label>
                    );
                  })}
                {data.clients.filter((c) => {
                  if (tagManagerFocusTag && !(c.tags || []).includes(tagManagerFocusTag)) return false;
                  if (!tagManagerSearch) return true;
                  const q = tagManagerSearch.toLowerCase();
                  return c.email.toLowerCase().includes(q) || (c.first_name || "").toLowerCase().includes(q) || (c.last_name || "").toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="p-4 text-xs text-gray-400 italic text-center">No clients match.</div>
                )}
              </div>
              {tagManagerSelected.length > 0 && (
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="font-bold text-[#4A0A66]" data-testid="tag-manager-selected-count">{tagManagerSelected.length} selected</span>
                  <button onClick={() => setTagManagerSelected([])} className="text-gray-400 hover:text-[#000000] font-bold uppercase tracking-[0.1em] text-[10px]">Clear</button>
                </div>
              )}

              {/* Quick-apply from library */}
              {tagCounts.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-2">Quick-apply from library {tagManagerSelected.length === 0 && <span className="text-gray-400 normal-case tracking-normal font-normal">· select clients first</span>}</div>
                  <div className="flex flex-wrap gap-2">
                    {tagCounts.map(([t]) => {
                      const col = tagColor(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          data-testid={`tag-manager-apply-${t}`}
                          disabled={tagManagerSelected.length === 0}
                          onClick={() => applyTagToSelected(t)}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold bg-white border-2 transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed hover:text-[color:var(--hover-fg)]"
                          style={{ borderColor: col.bg, color: col.bg, ["--hover-bg"]: col.bg, ["--hover-fg"]: col.fg }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = col.bg; e.currentTarget.style.color = col.fg; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.color = col.bg; }}
                          title={`Apply #${t} to ${tagManagerSelected.length} selected`}
                        >
                          + #{t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Or type a fresh tag */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-2">Or type a fresh tag</div>
                <div className="flex gap-2">
                  <input
                    data-testid="tag-manager-new-tag"
                    value={tagManagerNewTag}
                    onChange={(e) => setTagManagerNewTag(e.target.value)}
                    placeholder="e.g. High-Ticket"
                    maxLength={30}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!tagManagerNewTag.trim() || tagManagerSelected.length === 0) return;
                        await applyTagToSelected(tagManagerNewTag.trim());
                        setTagManagerNewTag("");
                      }
                    }}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                  />
                  <button
                    data-testid="tag-manager-add-btn"
                    disabled={!tagManagerNewTag.trim() || tagManagerSelected.length === 0}
                    onClick={async () => {
                      await applyTagToSelected(tagManagerNewTag.trim());
                      setTagManagerNewTag("");
                    }}
                    className="bg-[#4A0A66] text-white rounded-full px-5 py-2.5 font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#000000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Bulk-remove focused tag from selected */}
              {tagManagerFocusTag && tagManagerSelected.length > 0 && (
                <button
                  data-testid="tag-manager-remove-btn"
                  onClick={async () => {
                    await Promise.all(tagManagerSelected.map(async (cid) => {
                      const client = data.clients.find((c) => c.id === cid);
                      const next = (client.tags || []).filter((t) => t !== tagManagerFocusTag);
                      await api.put(`/admin/clients/${cid}`, { tags: next });
                    }));
                    toast.success(`Removed #${tagManagerFocusTag} from ${tagManagerSelected.length} client${tagManagerSelected.length === 1 ? "" : "s"}.`);
                    setTagManagerSelected([]);
                    load();
                  }}
                  className="mt-3 text-xs font-bold uppercase tracking-[0.1em] text-[#DF1283] hover:text-[#000000] transition-colors"
                >
                  × Remove #{tagManagerFocusTag} from {tagManagerSelected.length} selected
                </button>
              )}
            </div>
          </section>
        </DialogContent>
      </Dialog>

      {/* Access-code MEMBERS drill-in */}
      <Dialog open={!!membersOfCode} onOpenChange={(o) => { if (!o) setMembersOfCode(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="code-members-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display font-black flex items-center gap-2">
              <KeyRound size={20} style={{ color: PINK }} />
              Members on <span className="font-mono">{membersOfCode?.code}</span>
            </DialogTitle>
            <DialogDescription>
              {membersOfCode?.label && <span className="text-gray-600">{membersOfCode.label} · </span>}
              <span data-testid="code-members-count">{membersOfCode?.total ?? 0} of {membersOfCode?.max_uses ?? 0} seats used</span>
            </DialogDescription>
          </DialogHeader>

          {membersLoading && <div className="py-8 text-center text-sm text-gray-400">Loading members…</div>}

          {!membersLoading && membersOfCode?.total === 0 && (
            <div className="rounded-xl border-2 border-dashed border-gray-300 p-8 text-center">
              <Users size={32} className="mx-auto text-gray-300 mb-2" />
              <div className="text-sm font-bold text-gray-500">No one has used this code yet</div>
              <div className="text-xs text-gray-400 mt-1">Share the code or email it — new signups will appear here.</div>
            </div>
          )}

          {!membersLoading && membersOfCode?.members?.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Registered clients</span>
                <button
                  data-testid="code-members-remind-all-btn"
                  onClick={remindAllMembers}
                  className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white bg-[#4A0A66] rounded-full px-3 py-1.5 hover:bg-[#000000] transition-colors"
                  title="Email a sign-in reminder to every active member on this code"
                >
                  <Mail size={11} /> Email all
                </button>
              </div>
              <div className="space-y-2">
                {membersOfCode.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:border-[#0FFFE2] transition-colors" data-testid={`code-member-row-${m.email}`}>
                    <div className="w-8 h-8 rounded-full bg-[#4A0A66] text-white flex items-center justify-center text-xs font-black shrink-0">
                      {(m.first_name || m.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#000000] truncate">
                        {(m.title ? `${m.title} ` : "") + ([m.first_name, m.last_name].filter(Boolean).join(" ") || m.name || m.email)}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {m.email} · joined {m.registered_at ? new Date(m.registered_at).toLocaleDateString() : "—"}
                        {m.expires_at && (m.expired
                          ? <span className="ml-1.5 text-[#DF1283] font-bold">· Expired</span>
                          : <span className="ml-1.5 text-[#FC861E] font-bold">· {m.days_remaining} day{m.days_remaining === 1 ? "" : "s"} left</span>)}
                      </div>
                    </div>
                    {!m.access_enabled && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-[#DF1283] text-white">Disabled</span>
                    )}
                    <button
                      data-testid={`code-member-remind-${m.email}`}
                      onClick={() => remindOneMember(m)}
                      className="p-2 rounded-full text-gray-500 hover:bg-[#0FFFE2] hover:text-[#000000] transition-colors"
                      title="Send login help to this member"
                    >
                      <Mail size={14} />
                    </button>
                    <button
                      data-testid={`code-member-open-${m.email}`}
                      onClick={() => openMemberFromCode(m)}
                      className="p-2 rounded-full text-gray-500 hover:bg-[#4A0A66] hover:text-white transition-colors"
                      title="Open full client detail"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={refreshMembers}
                className="mt-3 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-400 hover:text-[#000000] transition-colors"
              >
                ↻ Refresh
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Access-code PDF handoff dialog */}
      <Dialog open={!!handoffCode} onOpenChange={(o) => { if (!o) setHandoffCode(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="handoff-pdf-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-display font-black flex items-center gap-2">
              <FileDown size={18} style={{ color: "#4A0A66" }} /> Handoff PDF
            </DialogTitle>
            <DialogDescription>
              Generate a branded one-pager for <span className="font-mono font-bold text-[#000000]">{handoffCode?.code}</span> ({handoffCode?.max_uses} seats). Forward it to the client so their team can activate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Client / company name</label>
              <input
                data-testid="handoff-client-name-input"
                value={handoffForm.client_name}
                onChange={(e) => setHandoffForm({ ...handoffForm, client_name: e.target.value })}
                placeholder="e.g. Acme Marketing, or the person's name"
                maxLength={60}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
              />
              <p className="text-[11px] text-gray-400 mt-1">Appears in the greeting: <em>Welcome aboard, {handoffForm.client_name || "your team"}!</em></p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Optional note <span className="font-normal normal-case text-gray-400 tracking-normal">· e.g. thank-you or reminders</span></label>
              <textarea
                data-testid="handoff-note-input"
                value={handoffForm.note}
                onChange={(e) => setHandoffForm({ ...handoffForm, note: e.target.value })}
                rows={3}
                maxLength={400}
                placeholder="Thank you for choosing UnCloned® — reach out anytime with questions."
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] resize-none"
              />
              <p className="text-[11px] text-gray-400 mt-1">{handoffForm.note.length}/400 · Rendered in a pumpkin-outlined callout above the support button.</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11px] text-gray-600">
              <div className="font-bold uppercase tracking-[0.1em] text-gray-500 mb-1">Included by default</div>
              UnCloned® logo · code (big) · {handoffCode?.max_uses ?? 0} seats · {handoffCode?.expires_at ? "expiration date · " : ""}numbered activation steps · QR code · <strong>Submit a support ticket</strong> button linking to unclonedsupport.com · footer with support link
            </div>
            <button
              data-testid="handoff-download-btn"
              onClick={downloadHandoffPdf}
              disabled={handoffLoading}
              className="w-full flex items-center justify-center gap-2 bg-[#4A0A66] text-white rounded-full px-6 py-3 font-bold text-sm hover:bg-[#000000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileDown size={14} /> {handoffLoading ? "Generating…" : "Download PDF"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
